/**
 * MetricsPage
 * Visualización de métricas del agente seleccionado
 */

import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  Stack,
  Divider,
  CircularProgress,
  Grid,
  Chip,
} from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import { useParams, useNavigate } from 'react-router-dom';
import { useAgent } from '@/hooks/useAgents';
import { useMetrics } from '@/hooks/useMetrics';
import { MetricsCard } from '@/components/metrics/MetricsCard';
import { ProcessList } from '@/components/metrics/ProcessList';
import PageHeader from '@/components/common/PageHeader';

export const MetricsPage: React.FC = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { data: agent, isLoading: agentLoading, error: agentError } = useAgent(agentId || null);
  const {
    data: metrics,
    isLoading: metricsLoading,
    error: metricsError,
    refetch,
  } = useMetrics(agentId || null);

  const isLoading = agentLoading || metricsLoading;

  const handleRefresh = () => {
    refetch();
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} height="100%">
      <PageHeader
        icon={<SpeedIcon />}
        title="Métricas"
        subtitle="Monitoreo en tiempo real del agente. Actualización cada 5 segundos."
        actions={
          <>
            <Button variant="outlined" size="small" onClick={handleRefresh} disabled={isLoading}>
              Actualizar
            </Button>
            <Button variant="outlined" onClick={() => navigate('/agents')}>
              Volver
            </Button>
          </>
        }
      />

      {(agentError || metricsError) && (
        <Alert severity="error">
          {agentError instanceof Error
            ? agentError.message
            : metricsError instanceof Error
              ? metricsError.message
              : 'Error cargando datos'}
        </Alert>
      )}

      {isLoading && !metrics ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2} sx={{ flex: 1, overflow: 'auto' }}>
          {/* Agent Info Card */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {agent?.hostname || agentId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {agent ? `${agent.os || '—'} ${agent.arch || ''}` : 'Cargando…'}
                  </Typography>
                </Box>
                <Chip
                  label={agent?.connected ? 'Conectado' : 'Offline'}
                  color={agent?.connected ? 'success' : 'default'}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* System Metrics */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Recursos del Sistema
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {metrics ? (
                <>
                  <MetricsCard label="CPU" value={metrics.cpuPercent} format="percent" />
                  <MetricsCard
                    label="Memoria RAM"
                    value={metrics.memUsedBytes}
                    max={metrics.memTotalBytes}
                    format="bytes"
                  />
                  {metrics.disks && metrics.disks.length > 0 ? (
                    metrics.disks.map((d) => (
                      <MetricsCard
                        key={d.path}
                        label={`Disco ${d.path}`}
                        value={d.usedBytes}
                        max={d.totalBytes}
                        format="bytes"
                      />
                    ))
                  ) : (
                    <MetricsCard
                      label="Disco"
                      value={metrics.diskUsedBytes}
                      max={metrics.diskTotalBytes}
                      format="bytes"
                    />
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Cargando métricas…
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Application Info */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Información
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    App Activa
                  </Typography>
                  <Typography variant="body1">
                    {metrics?.activeApp ? (
                      metrics.activeApp.available ? (
                        <>
                          {metrics.activeApp.name} (PID: {metrics.activeApp.pid})
                        </>
                      ) : (
                        <>No disponible ({metrics.activeApp.reason})</>
                      )
                    ) : (
                      '—'
                    )}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Procesos en Ejecución
                  </Typography>
                  <Typography variant="body1">{metrics?.processes?.length || 0}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Versión Agente
                  </Typography>
                  <Typography variant="body1">{agent?.version || '—'}</Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Process List */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Procesos (Top 10 por CPU)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {metrics?.processes ? (
                <ProcessList processes={metrics.processes} maxItems={10} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Cargando procesos…
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};
